import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AadeBookInvoiceType } from '../../../../interfaces/myData.interface';
import { invTypeI18nKey } from '../../../../interfaces/myDataInvTypes';

interface Props {
  doc: AadeBookInvoiceType;
}

const MetaRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex-start-center-row-nowrap margin-b-4">
    <div className="boldText4 padding-r-6" style={{ whiteSpace: 'nowrap' }}>{label}:</div>
    <div className="normalText4" style={{ wordBreak: 'break-all' }}>{value}</div>
  </div>
);


export const GsisDocMeta = ({ doc }: Props) => {
  const { t } = useTranslation();

  const getDeliveryStatus = useCallback(() => {
    if (doc?.invoiceDeliveryStatus) {
      return <div className="mediumText4 text-success">{t(`DELIVERY.STATUS.${doc?.invoiceDeliveryStatus}`)}</div>;
    }
  }, [doc?.invoiceDeliveryStatus, t]);

  if (!doc) return null;

  const h = doc.invoiceHeader;

  let issueDate: string | null = null;
  if (h?.issueDate) {
    try {
      issueDate = new Date(h.issueDate as any).toLocaleString();
    } catch {
      issueDate = String(h.issueDate);
    }
  }

  const hasDeliverySection =
    h?.isDeliveryNote ||
    h?.movePurpose != null ||
    h?.dispatchDate ||
    h?.dispatchTime ||
    h?.vehicleNumber ||
    h?.otherDeliveryNoteHeader;

  return (
    <div className="simpleBorder p-12 width-100">
      {/* Header row: title, links, status */}
      <div className="flex-space-between-start-row-wrap margin-b-8 width-100">
        <div className="flex-start-center-row-nowrap gap-10 flex-wrap">
          <div className="boldText5">{t('DELIVERY.DOCUMENT_META')}</div>
          {doc.qrCodeUrl && (
            <a href={doc.qrCodeUrl} target="_blank" rel="noopener noreferrer" className="normalText4 text-blue link-reset">
              {t('DELIVERY.QR_LINK')}
            </a>
          )}
          {doc.downloadingInvoiceUrl && (
            <a href={doc.downloadingInvoiceUrl} target="_blank" rel="noopener noreferrer" className="normalText4 text-blue link-reset">
              {t('DELIVERY.PROVIDER_URL')}
            </a>
          )}
        </div>
        {getDeliveryStatus()}
      </div>

      {/* Core identifiers */}
      <div className="flex-start-start-row-wrap gap-12 margin-b-4">
        <MetaRow label={t('DELIVERY.MARK')} value={doc.mark || '-'} />
        <MetaRow label={t('DELIVERY.UID')} value={doc.uid || '-'} />
        {doc.cancelledByMark != null && (
          <MetaRow label={t('DELIVERY.CANCELLED_BY_MARK')} value={String(doc.cancelledByMark)} />
        )}
        {doc.authenticationCode && (
          <MetaRow label={t('DELIVERY.AUTH_CODE')} value={doc.authenticationCode} />
        )}
        {doc.transmissionFailure != null && (
          <MetaRow label={t('DELIVERY.TRANSMISSION_FAILURE')} value={String(doc.transmissionFailure)} />
        )}
      </div>

      {/* Invoice header fields */}
      <div className="flex-start-start-row-wrap gap-12 margin-b-4">
        {h?.invoiceType && (
          <MetaRow
            label={t('DELIVERY.INVOICE_TYPE')}
            value={`${h.invoiceType} – ${t(invTypeI18nKey(h.invoiceType), { defaultValue: h.invoiceType })}`}
          />
        )}
        <MetaRow label={t('DELIVERY.SERIES')} value={h?.series || '-'} />
        <MetaRow label={t('DELIVERY.AA')} value={h?.aa || '-'} />
        <MetaRow label={t('DELIVERY.ISSUE_DATE')} value={issueDate || '-'} />
      </div>

      {/* Optional invoice-level flags */}
      <div className="flex-start-start-row-wrap gap-12">
        {h?.exchangeRate != null && <MetaRow label={t('DELIVERY.EXCHANGE_RATE')} value={String(h.exchangeRate)} />}
        {h?.correlatedInvoices && <MetaRow label={t('DELIVERY.CORRELATED_INVOICES')} value={h.correlatedInvoices} />}
        {h?.vatPaymentSuspension && <MetaRow label={t('DELIVERY.VAT_PAYMENT_SUSPENSION')} value={t('DELIVERY.YES')} />}
        {h?.selfPricing && <MetaRow label={t('DELIVERY.SELF_PRICING')} value={t('DELIVERY.YES')} />}
        {h?.fuelInvoice && <MetaRow label={t('DELIVERY.FUEL_INVOICE')} value={t('DELIVERY.YES')} />}
        {h?.specialInvoiceCategory != null && (
          <MetaRow label={t('DELIVERY.SPECIAL_INVOICE_CATEGORY')} value={String(h.specialInvoiceCategory)} />
        )}
        {h?.invoiceVariationType != null && (
          <MetaRow label={t('DELIVERY.INVOICE_VARIATION_TYPE')} value={String(h.invoiceVariationType)} />
        )}
      </div>

      {/* Delivery-specific section — visually separated */}
      {hasDeliverySection && (
        <div style={{ borderTop: '1px solid var(--ion-border-color, #333)', marginTop: 10, paddingTop: 10 }}>
          <div className="boldText5 margin-b-6">{t('DELIVERY.DELIVERY_FLOW')}</div>

          <div className="flex-start-start-row-wrap gap-12 margin-b-4">
            {h?.isDeliveryNote && (
              <MetaRow label={t('DELIVERY.IS_DELIVERY_NOTE')} value={t('DELIVERY.YES')} />
            )}
            {h?.movePurpose != null && (
              <MetaRow
                label={t('DELIVERY.MOVE_PURPOSE')}
                value={t(`DELIVERY.MOVE_PURPOSE_${h.movePurpose}`, { defaultValue: String(h.movePurpose) })}
              />
            )}
            {h?.dispatchDate && <MetaRow label={t('DELIVERY.DISPATCH_DATE')} value={String(h.dispatchDate)} />}
            {h?.dispatchTime && <MetaRow label={t('DELIVERY.DISPATCH_TIME')} value={String(h.dispatchTime)} />}
            {h?.vehicleNumber && <MetaRow label={t('DELIVERY.VEHICLE_NUMBER')} value={h.vehicleNumber} />}
          </div>

          {h?.otherDeliveryNoteHeader && (
            <div className="flex-start-start-row-wrap gap-16 margin-t-6">
              {/* Origin */}
              <div style={{ flex: '1 1 200px' }}>
                <div className="boldText4 margin-b-4">{t('DELIVERY.LOADING_ADDRESS')}</div>
                <div className="normalText4">
                  {[h.otherDeliveryNoteHeader.loadingAddress?.street, h.otherDeliveryNoteHeader.loadingAddress?.number]
                    .filter(Boolean)
                    .join(' ')}
                </div>
                <div className="normalText4">
                  {[h.otherDeliveryNoteHeader.loadingAddress?.postalCode, h.otherDeliveryNoteHeader.loadingAddress?.city]
                    .filter(Boolean)
                    .join(' ')}
                </div>
                {h.otherDeliveryNoteHeader.startShippingBranch != null && (
                  <div className="normalText4 margin-t-4">
                    {t('DELIVERY.BRANCH')}: {h.otherDeliveryNoteHeader.startShippingBranch}
                  </div>
                )}
              </div>

              {/* Destination */}
              <div style={{ flex: '1 1 200px' }}>
                <div className="boldText4 margin-b-4">{t('DELIVERY.DELIVERY_ADDRESS')}</div>
                <div className="normalText4">
                  {[h.otherDeliveryNoteHeader.deliveryAddress?.street, h.otherDeliveryNoteHeader.deliveryAddress?.number]
                    .filter(Boolean)
                    .join(' ')}
                </div>
                <div className="normalText4">
                  {[h.otherDeliveryNoteHeader.deliveryAddress?.postalCode, h.otherDeliveryNoteHeader.deliveryAddress?.city]
                    .filter(Boolean)
                    .join(' ')}
                </div>
                {h.otherDeliveryNoteHeader.completeShippingBranch != null && (
                  <div className="normalText4 margin-t-4">
                    {t('DELIVERY.BRANCH')}: {h.otherDeliveryNoteHeader.completeShippingBranch}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
